// Console d'administration (super admins) : la file de validation des
// e-mails. Rien ne part sans validation, sauf les envois directs du serveur
// (codes de connexion, alertes de validation, adresses de suivi d'une app).
import type { JSX } from 'preact'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { FeedbaacksError, type AppSummary, type EmailCounts, type EmailDetail, type EmailSummary } from '@feedbaacks/sdk'
import { absoluteDate, Button, ChipRow, errorMessage, formatRelative, IconButton, Icons, Sheet, Spinner, useFeedbaacks } from '@feedbaacks/ui'
import { d } from '../i18n'
import { appLabel, bulkTone, OUTBOX_TABS, sourceLabel, statusNote, toggle, toggleAll, type OutboxTab } from './superheroes-model'

/** Refus avec motif facultatif, habillé comme la confirmation partagée. */
function RejectDialog({ count, onCancel, onConfirm }: { count: number; onCancel: () => void; onConfirm: (reason: string) => void }): JSX.Element {
  const { t } = useFeedbaacks()
  const [reason, setReason] = useState('')
  const input = useRef<HTMLTextAreaElement>(null)
  useEffect(() => input.current?.focus(), [])
  const title = count > 1 ? d('outbox_reject_selection', { n: count }) : d('outbox_reject_title')
  return (
    <div class="fb-confirm-backdrop" onClick={onCancel}>
      <form class="fb-confirm" role="dialog" aria-modal="true" aria-label={title} data-testid="dash-outbox-reject-dialog"
        onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        onSubmit={(e) => {
          e.preventDefault()
          onConfirm(reason.trim())
        }}>
        <h3>{title}</h3>
        <p>{d('outbox_reject_message')}</p>
        <textarea class="fb-textarea" ref={input} aria-label={d('outbox_reject_reason')} placeholder={d('outbox_reject_reason')} value={reason}
          onInput={(e) => setReason(e.currentTarget.value)} data-testid="dash-outbox-reject-reason" />
        <div class="fb-confirm-actions">
          <Button variant="ghost" onClick={onCancel}>{t('cancel')}</Button>
          <Button type="submit" variant="danger" testId="dash-outbox-reject-confirm">{d('outbox_reject')}</Button>
        </div>
      </form>
    </div>
  )
}

export function Superheroes({ apps, onChanged }: { apps: AppSummary[]; onChanged: () => void }): JSX.Element {
  const { client, notify, confirm, t, locale } = useFeedbaacks()
  const [tab, setTab] = useState<OutboxTab>('pending')
  const [rows, setRows] = useState<EmailSummary[] | null>(null)
  const [counts, setCounts] = useState<EmailCounts | null>(null)
  const [required, setRequired] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<{ email: EmailSummary; detail: EmailDetail | null } | null>(null)
  const [rejecting, setRejecting] = useState<EmailSummary[] | null>(null)

  const load = useCallback(() => client.admin.emailOutbox.list({ status: tab }).then(
    (p) => {
      setRows(p.data)
      setCounts(p.counts)
      setRequired(p.approval_required)
      setError(null)
    },
    (err: unknown) => setError(errorMessage(err, d('error_generic'))),
  ), [client, tab])
  useEffect(() => {
    setRows(null)
    setSelected([])
    void load()
  }, [load])

  // Un geste : occupé pendant l'appel, puis la liste et la pastille du menu
  // sont relues, que le geste ait réussi ou non (un 409 = déjà traité).
  const run = async (gesture: () => Promise<void>) => {
    setBusy(true)
    try {
      await gesture()
    } catch (err) {
      notify('error', err instanceof FeedbaacksError && err.status === 409 ? d('outbox_conflict') : errorMessage(err, d('error_generic')))
    } finally {
      setBusy(false)
      setPreview(null)
      setSelected([])
      void load()
      onChanged()
    }
  }
  const approve = (e: EmailSummary) => run(async () => {
    const r = await client.admin.emailOutbox.approve(e.id)
    if (r.status === 'failed') notify('error', d('outbox_send_failed', { error: r.error_message ?? '' }))
    else notify('success', d('outbox_sent'))
  })
  const approveSelection = async () => {
    const title = d('outbox_approve_selection', { n: selected.length })
    if (!(await confirm({ title, message: d('outbox_approve_selection_message', { n: selected.length }), confirmLabel: d('outbox_approve') }))) return
    await run(async () => {
      const r = await client.admin.emailOutbox.approveMany(selected)
      notify(bulkTone(r.approved, r.failed), d('outbox_bulk_approved', r))
    })
  }
  const reject = (targets: EmailSummary[], reason: string) => run(async () => {
    if (targets.length === 1) {
      await client.admin.emailOutbox.reject(targets[0].id, reason)
      notify('success', d('outbox_rejected'))
    } else {
      const r = await client.admin.emailOutbox.rejectMany(targets.map((e) => e.id), reason)
      notify(bulkTone(r.rejected, r.failed), d('outbox_bulk_rejected', r))
    }
  })
  const openPreview = (email: EmailSummary) => {
    setPreview({ email, detail: null })
    client.admin.emailOutbox.get(email.id).then(
      (detail) => setPreview((p) => (p?.email.id === email.id ? { email, detail } : p)),
      (err: unknown) => {
        notify('error', errorMessage(err, d('error_generic')))
        setPreview(null)
      },
    )
  }

  const ids = rows?.map((e) => e.id) ?? []
  const meta = (e: EmailSummary, when: string) => [sourceLabel(e.source, d), appLabel(apps, e.app_key), when].filter(Boolean).join(' · ')
  // Refuser depuis l'aperçu le referme : une seule fenêtre à la fois.
  const actions = (e: EmailSummary, variant: 'primary' | 'secondary') => (
    <>
      {e.status === 'pending' && (
        <Button variant="ghost" disabled={busy} testId="dash-outbox-reject" onClick={() => {
          setPreview(null)
          setRejecting([e])
        }}>{d('outbox_reject')}</Button>
      )}
      {(e.status === 'pending' || e.status === 'failed') && (
        <Button variant={variant} disabled={busy} onClick={() => void approve(e)} testId="dash-outbox-approve">
          {d(e.status === 'failed' ? 'outbox_retry' : 'outbox_approve')}
        </Button>
      )}
    </>
  )

  return (
    <div class="dash-page is-wide" data-testid="dash-superheroes">
      <section class="dash-card">
        <h2 class="fb-section-title">{d('outbox_title')}</h2>
        <p class="fb-muted">{d('outbox_intro')}</p>
        {!required && <div class="dash-fresh" role="status" data-testid="dash-outbox-disabled">{d('outbox_disabled')}</div>}
        <ChipRow label={d('outbox_tabs')} value={tab} onChange={setTab}
          options={OUTBOX_TABS.map((s) => ({ value: s, label: d(`outbox_tab_${s}`), count: counts?.[s] }))} />
        {tab === 'pending' && ids.length > 0 && (
          <div class="dash-outbox-bar">
            <label class="fb-check">
              <input type="checkbox" checked={selected.length === ids.length} onChange={() => setSelected(toggleAll(selected, ids))} data-testid="dash-outbox-select-all" />
              <span>{d('outbox_select_all')}</span>
            </label>
            <span class="fb-grow" />
            {selected.length > 0 && (
              <>
                <Button variant="ghost" disabled={busy} onClick={() => setRejecting((rows ?? []).filter((e) => selected.includes(e.id)))} testId="dash-outbox-reject-selection">
                  {d('outbox_reject_selection', { n: selected.length })}
                </Button>
                <Button variant="primary" disabled={busy} onClick={() => void approveSelection()} testId="dash-outbox-approve-selection">
                  {d('outbox_approve_selection', { n: selected.length })}
                </Button>
              </>
            )}
          </div>
        )}
        {error ? (
          <div class="dash-outbox-bar" role="alert" data-testid="dash-outbox-error">
            <span class="fb-muted fb-grow">{error}</span>
            <Button onClick={() => void load()}>{d('outbox_reload')}</Button>
          </div>
        ) : !rows ? (
          <div class="dash-center"><Spinner size={24} /></div>
        ) : rows.length === 0 ? (
          <p class="dash-center fb-muted" data-testid="dash-outbox-empty">{d(`outbox_empty_${tab}`)}</p>
        ) : (
          <ul class="dash-list" data-testid="dash-outbox-list">
            {rows.map((e) => {
              const note = statusNote(e, d, (iso) => absoluteDate(iso, locale))
              return (
                <li key={e.id} data-testid="dash-outbox-row" data-id={e.id}>
                  {tab === 'pending' && (
                    <input type="checkbox" checked={selected.includes(e.id)} aria-label={d('outbox_select', { recipient: e.recipient })}
                      onChange={() => setSelected(toggle(selected, e.id))} data-testid="dash-outbox-select" />
                  )}
                  <div class="dash-outbox-main">
                    <span class="fb-strong">{e.recipient}</span>
                    <span>{e.subject}</span>
                    <span class="fb-faint" title={absoluteDate(e.created_at, locale)}>{meta(e, formatRelative(e.created_at, locale, t))}</span>
                    {note && <span class="fb-faint" data-testid="dash-outbox-note">{note}</span>}
                  </div>
                  <Button variant="ghost" onClick={() => openPreview(e)} testId="dash-outbox-open">{d('outbox_preview')}</Button>
                  {actions(e, 'secondary')}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <Sheet open={!!preview} onClose={() => setPreview(null)} label={d('outbox_preview')}>
        <div class="dash-outbox-preview" data-testid="dash-outbox-preview">
          {preview && (
            <>
              <div class="dash-outbox-bar">
                <h2 class="fb-strong fb-grow">{preview.email.subject}</h2>
                <IconButton label={t('close')} onClick={() => setPreview(null)}><Icons.X size={16} /></IconButton>
              </div>
              <p>{d('outbox_to', { recipient: preview.email.recipient })}</p>
              <p class="fb-faint">{meta(preview.email, absoluteDate(preview.email.created_at, locale))}</p>
              {!preview.detail ? (
                <div class="dash-center"><Spinner size={24} /></div>
              ) : preview.detail.body_html ? (
                // Le HTML de l'e-mail n'entre jamais dans la page : cadre isolé, sans script.
                <iframe class="dash-outbox-frame" sandbox="" srcdoc={preview.detail.body_html} title={preview.email.subject} data-testid="dash-outbox-frame" />
              ) : (
                <pre class="dash-code-block fb-grow" data-testid="dash-outbox-text">{preview.detail.body_text}</pre>
              )}
              <div class="dash-outbox-bar"><span class="fb-grow" />{actions(preview.email, 'primary')}</div>
            </>
          )}
        </div>
      </Sheet>
      {rejecting && (
        <RejectDialog count={rejecting.length} onCancel={() => setRejecting(null)} onConfirm={(reason) => {
          setRejecting(null)
          void reject(rejecting, reason)
        }} />
      )}
    </div>
  )
}
